import { createSelector } from "reselect"
import { get, last, first, map, chain, size, some } from "lodash"
import { getDatesAdjusted as getDatesAdjustedHelper } from "helpers/dates"
import { getPeriod, getMustAggregateFactors } from "modules/portfolio-root/selectors/portfolios"
import { getModelFactorsClassification } from "selectors/modelMetadata"
import { getLatestDate } from "modules/portfolio-root/selectors/dates"
import { getDefaultMetricUnits } from "modules/default-metric-units/selectors"
import { getAllResourcesStatuses } from "selectors/data"
import { getEndDate } from "selectors/dates"
import { getSelectedViews, getSelectedViewsFactors } from "selectors/factors"
import { aggregateFactorsSumBy } from "helpers/factorClassification/index"
import { computeFactorCategories } from "helpers/factors"
import { METRIC_TYPES } from "helpers/assets/constants"
import { mapPerformanceAttributionFactorReturnsToValues } from "helpers/contributors"
import { combineResourcesStatuses, isLoading } from "helpers/data-statuses"

export const getRawPerformanceHistory = state => get(state, "global.data.analyzePerformance.data.model.simulation.performance")
const getRawMetricType = state => get(state, "modules.analyzePerformance.selectedMetricType")

export const getMetricType = createSelector(
  [getDefaultMetricUnits, getRawMetricType],
  (units, metricType) => {
    return metricType || get(units, "performance", METRIC_TYPES.NET_CONTRIBUTION)
  },
)

const getPerformanceMetricField = createSelector(
  [getMetricType],
  (metricType) => metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "cumulativeCurrency" : "cumulativePercent",
)

const getAdjustedPerformanceHistory = createSelector([getRawPerformanceHistory, getPerformanceMetricField], mapPerformanceAttributionFactorReturnsToValues)

export const getPerformanceHistory = createSelector(
  [getAdjustedPerformanceHistory, getMustAggregateFactors, getSelectedViews, getSelectedViewsFactors, getModelFactorsClassification, getPerformanceMetricField],
  (performanceHistory, mustAggregateFactors, selectedViews, selectedViewsFactors, factorsClassification, performanceMetricField) => map(performanceHistory, daily => {
    let factors = get(daily, "attribution.factors")
    if (mustAggregateFactors) {
      factors = aggregateFactorsSumBy(factorsClassification, factors, "value")
    }

    if (size(selectedViews)) {
      factors = chain(daily)
        .get("attribution.factors")
        .filter(factor => some(selectedViewsFactors, item => item.id === factor.id))
        .map(factor => ({ ...factor, customView: true }))
        .concat(factors)
        .value()
    }

    return {
      date: daily.date,
      total: get(daily, ["totalReturn", performanceMetricField]),
      factorsTotal: get(daily, ["attribution", "summary", "factors", performanceMetricField]),
      alpha: get(daily, ["attribution", "summary", "specific", performanceMetricField]),
      trading: get(daily, ["attribution", "summary", "trading", performanceMetricField]),
      factors: chain(factors)
        .keyBy("id")
        .mapValues(factor => get(factor, "value"))
        .value(),
      categories: computeFactorCategories(factors, selectedViews),
    }
  }),
)

export const getCurrentPerformance = createSelector(
  [getPerformanceHistory],
  (performanceHistory) => last(performanceHistory),
)

export const getFirstPerformanceDate = createSelector(
  [getPerformanceHistory],
  (performanceHistory) => get(first(performanceHistory), "date", null),
)

export const getPassthroughResourcesStatus = createSelector(
  [getAllResourcesStatuses],
  (statuses) => combineResourcesStatuses("analyzePerformance", statuses),
)

export const getIsLoading = createSelector(
  [getPassthroughResourcesStatus],
  isLoading,
)

export const getDatesAdjusted = createSelector(
  [getPeriod, getFirstPerformanceDate, getEndDate, getLatestDate, getIsLoading],
  (period, startDate, endDate, latestDate, isLoading) => getDatesAdjustedHelper(period, startDate, endDate, latestDate) && !isLoading,
)
